/**
 * @fileoverview Theme Management Modal for Personal Quick Reaction ("Reacción Rápida Personal" - Tile ID 45).
 * Enables creating, editing, selecting, and deleting graphic themes with SkiaSharp media optimization.
 * Supports Drag & Drop and native file uploads for:
 *   - Screen background 1080p WebP (`background`)
 *   - Room logo PNG RGBA8888 (`logo`)
 *   - The 4 character state sprites 500x500 (`sprite_back`, `sprite_front`, `sprite_shoot`, `sprite_victory`)
 *   - Broadcast overlay banners (`banner_1536`, `banner_1920`)
 * 
 * @module components/Admin/Overlays/QuickReactionPersonal/QuickReactionPersonalThemeModal
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage';
import {
    GetThemes,
    SaveTheme,
    DeleteTheme,
    UploadAsset,
    resolveMediaUrl
} from './../../../../utilities/BL/PersonalQuickReaction';

/**
 * Drag & Drop Image Field Component with instant preview.
 * 
 * @function AssetDropField
 * @param {Object} props
 * @param {string} props.label - User-facing field label.
 * @param {string} props.assetType - SkiaSharp processing category.
 * @param {string} props.currentValue - Relative or absolute image path.
 * @param {Function} props.onFileUploaded - Callback when upload completes.
 * @param {boolean} [props.isUploading=false] - Spinner flag.
 * @param {string} [props.helpText] - Recommended dimensions / format.
 * @param {Function} [props.onClear] - Clear asset callback.
 */
const AssetDropField = ({
    label,
    assetType,
    currentValue,
    onFileUploaded,
    isUploading = false,
    helpText = '',
    onClear = null
}) => {
    const [isHovered, setIsHovered] = useState(false);

    const handleFile = (file) => {
        if (file) {
            onFileUploaded(file, assetType);
        }
    };

    return (
        <div className="QuickReactionPersonalThemeModal__Field mb-3">
            <div className="d-flex align-items-center justify-content-between mb-1">
                <label className="form-label fw-bold text-dark small mb-0">
                    {label}
                </label>
                {currentValue && onClear && (
                    <button
                        type="button"
                        className="btn btn-link text-danger p-0 small text-decoration-none"
                        onClick={onClear}
                        title="Limpiar imagen"
                    >
                        <i className="bi bi-trash me-1"></i>Remover
                    </button>
                )}
            </div>

            <div
                className={`border border-2 border-dashed rounded-3 p-3 text-center cursor-pointer transition ${
                    isHovered ? 'border-danger bg-danger-subtle' : 'border-secondary-subtle bg-light'
                }`}
                onDragOver={(e) => { e.preventDefault(); setIsHovered(true); }}
                onDragLeave={() => setIsHovered(false)}
                onDrop={(e) => {
                    e.preventDefault();
                    setIsHovered(false);
                    if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
                }}
                onClick={() => document.getElementById(`upload-${assetType}`)?.click()}
                style={{ minHeight: '85px', cursor: 'pointer' }}
            >
                {isUploading ? (
                    <div className="py-2">
                        <div className="spinner-border spinner-border-sm text-danger me-2" role="status"></div>
                        <span className="small text-danger fw-bold">Optimizando con SkiaSharp...</span>
                    </div>
                ) : (
                    <>
                        <i className="bi bi-cloud-arrow-up-fill text-danger fs-4 d-block mb-1"></i>
                        <span className="small text-muted d-block">
                            Arrastra aquí tu imagen o <strong className="text-danger">haz clic</strong>
                        </span>
                        {helpText && <small className="text-secondary d-block mt-1">{helpText}</small>}
                    </>
                )}
                <input
                    id={`upload-${assetType}`}
                    type="file"
                    className="d-none"
                    accept="image/*"
                    onChange={(e) => {
                        if (e.target.files?.[0]) handleFile(e.target.files[0]);
                    }}
                />
            </div>

            {currentValue && (
                <div className="mt-2 text-center bg-white p-2 rounded-3 border shadow-sm position-relative">
                    <img
                        src={resolveMediaUrl(currentValue)}
                        alt={label}
                        className="img-fluid rounded"
                        style={{ maxHeight: '110px', objectFit: 'contain' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className="small text-muted text-truncate mt-1" style={{ fontSize: '0.75rem' }}>
                        {currentValue}
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Personal Quick Reaction Theme Manager Modal.
 * 
 * @function QuickReactionPersonalThemeModal
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is visible.
 * @param {Function} props.onClose - Close callback.
 * @param {string} props.roomId - Current room GUID.
 * @param {string} [props.activeThemeId] - Currently active theme identifier.
 * @param {Function} props.onThemeSelected - Callback when active theme changes.
 * @returns {React.ReactElement|null} Modal markup.
 */
export const QuickReactionPersonalThemeModal = ({
    isOpen,
    onClose,
    roomId,
    activeThemeId,
    onThemeSelected
}) => {
    const { showMessage } = useFloatMessage();

    // Data states
    const [themesList, setThemesList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [uploadingField, setUploadingField] = useState(null);

    // Form editing state
    const [isCreatingNew, setIsCreatingNew] = useState(false);
    const [editingThemeId, setEditingThemeId] = useState(null);
    const [themeForm, setThemeForm] = useState({
        name: 'Tema Oficial ADSL',
        backgroundImageUrl: '',
        logoImageUrl: '',
        spriteBackUrl: '',
        spriteFrontUrl: '',
        spriteShootUrl: '',
        spriteVictoryUrl: '',
        banner1536Url: '',
        banner1920Url: ''
    });

    const loadThemes = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await GetThemes(roomId);
            if (res.success && Array.isArray(res.data)) {
                setThemesList(res.data);
            }
        } catch {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al cargar catálogo de temas.' });
        } finally {
            setIsLoading(false);
        }
    }, [roomId, showMessage]);

    useEffect(() => {
        if (isOpen) {
            loadThemes();
            setIsCreatingNew(false);
            setEditingThemeId(null);
        }
    }, [isOpen, loadThemes]);

    if (!isOpen) return null;

    const handleSelectForEdit = (theme) => {
        setIsCreatingNew(false);
        setEditingThemeId(theme.id);
        setThemeForm({
            name: theme.name || 'Tema Personalizado',
            backgroundImageUrl: theme.backgroundImageUrl || '',
            logoImageUrl: theme.logoImageUrl || '',
            spriteBackUrl: theme.spriteBackUrl || '',
            spriteFrontUrl: theme.spriteFrontUrl || '',
            spriteShootUrl: theme.spriteShootUrl || '',
            spriteVictoryUrl: theme.spriteVictoryUrl || '',
            banner1536Url: theme.banner1536Url || '',
            banner1920Url: theme.banner1920Url || ''
        });
    };

    const handleStartNew = () => {
        setIsCreatingNew(true);
        setEditingThemeId(null);
        setThemeForm({
            name: `Nuevo Tema (${new Date().toLocaleDateString('es-MX')})`,
            backgroundImageUrl: '',
            logoImageUrl: '',
            spriteBackUrl: '',
            spriteFrontUrl: '',
            spriteShootUrl: '',
            spriteVictoryUrl: '',
            banner1536Url: '',
            banner1920Url: ''
        });
    };

    const handleUploadAsset = async (file, assetType) => {
        setUploadingField(assetType);
        try {
            const res = await UploadAsset(file, roomId, assetType);
            if (res.success && res.data?.url) {
                const uploadedUrl = res.data.url;
                setThemeForm(prev => {
                    const updated = { ...prev };
                    if (assetType === 'background') updated.backgroundImageUrl = uploadedUrl;
                    else if (assetType === 'logo') updated.logoImageUrl = uploadedUrl;
                    else if (assetType === 'sprite_back') updated.spriteBackUrl = uploadedUrl;
                    else if (assetType === 'sprite_front') updated.spriteFrontUrl = uploadedUrl;
                    else if (assetType === 'sprite_shoot') updated.spriteShootUrl = uploadedUrl;
                    else if (assetType === 'sprite_victory') updated.spriteVictoryUrl = uploadedUrl;
                    else if (assetType === 'banner_1536') updated.banner1536Url = uploadedUrl;
                    else if (assetType === 'banner_1920') updated.banner1920Url = uploadedUrl;
                    return updated;
                });
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `Archivo "${file.name}" procesado con éxito por SkiaSharp.`
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res.message || 'Error al procesar la imagen en el servidor.'
                });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: error.message || 'Error de conexión.' });
        } finally {
            setUploadingField(null);
        }
    };

    const handleSaveTheme = async (e) => {
        e.preventDefault();
        if (!themeForm.name.trim()) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'El nombre del tema es obligatorio.' });
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                id: editingThemeId || undefined,
                roomId,
                ...themeForm
            };
            const res = await SaveTheme(payload);
            if (res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `Tema "${themeForm.name}" guardado exitosamente.`
                });
                await loadThemes();
                setIsCreatingNew(false);
                setEditingThemeId(null);
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al guardar tema.' });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: error.message || 'Error de red.' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteTheme = async (themeId, themeName) => {
        if (!window.confirm(`¿Estás seguro de eliminar el tema "${themeName}"?`)) return;

        try {
            const res = await DeleteTheme(roomId, themeId);
            if (res.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Tema eliminado correctamente.' });
                await loadThemes();
                if (editingThemeId === themeId) {
                    setIsCreatingNew(false);
                    setEditingThemeId(null);
                }
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al eliminar tema.' });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: error.message || 'Error de red.' });
        }
    };

    return (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 1060 }}>
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content border-0 shadow-lg rounded-4">
                    {/* Header */}
                    <div className="modal-header bg-dark text-white border-bottom border-secondary">
                        <h5 className="modal-title fw-bold d-flex align-items-center gap-2">
                            <i className="bi bi-palette-fill text-danger"></i>
                            Gestor de Temas Visuales y Sprites (SkiaSharp)
                        </h5>
                        <button type="button" className="btn-close btn-close-white" onClick={onClose} aria-label="Close"></button>
                    </div>

                    {/* Body */}
                    <div className="modal-body p-4 bg-light">
                        <div className="row g-4">
                            {/* Columna Izquierda: Lista de Temas */}
                            <div className="col-12 col-lg-4 border-end-lg">
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <h6 className="fw-bold text-dark text-uppercase small mb-0">
                                        Temas Disponibles ({themesList.length})
                                    </h6>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-danger fw-bold d-flex align-items-center gap-1"
                                        onClick={handleStartNew}
                                    >
                                        <i className="bi bi-plus-lg"></i> Nuevo Tema
                                    </button>
                                </div>

                                {isLoading ? (
                                    <div className="text-center py-4 text-muted">
                                        <div className="spinner-border text-danger spinner-border-sm me-2"></div>
                                        Cargando catálogo...
                                    </div>
                                ) : themesList.length === 0 ? (
                                    <div className="text-center py-4 bg-white rounded-3 border text-muted small">
                                        No hay temas personalizados creados.
                                    </div>
                                ) : (
                                    <div className="d-flex flex-column gap-2" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                        {themesList.map((t) => {
                                            const isActive = activeThemeId === t.id;
                                            const isSelected = editingThemeId === t.id;
                                            return (
                                                <div
                                                    key={t.id}
                                                    className={`card p-3 rounded-3 border transition cursor-pointer ${
                                                        isSelected
                                                            ? 'border-danger bg-danger-subtle shadow-sm'
                                                            : isActive
                                                            ? 'border-success bg-white shadow-sm'
                                                            : 'border-light bg-white hover-shadow'
                                                    }`}
                                                    onClick={() => handleSelectForEdit(t)}
                                                >
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <span className="fw-bold text-dark text-truncate" style={{ maxWidth: '170px' }}>
                                                            {t.name}
                                                        </span>
                                                        {isActive && (
                                                            <span className="badge bg-success small">
                                                                <i className="bi bi-check-circle-fill me-1"></i> Activo
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* Vista previa miniatura */}
                                                    <div className="d-flex gap-1 mb-2">
                                                        {t.spriteFrontUrl && (
                                                            <img
                                                                src={resolveMediaUrl(t.spriteFrontUrl)}
                                                                alt="Sprite 2"
                                                                className="rounded bg-light border"
                                                                style={{ width: '38px', height: '38px', objectFit: 'contain' }}
                                                            />
                                                        )}
                                                        {t.spriteShootUrl && (
                                                            <img
                                                                src={resolveMediaUrl(t.spriteShootUrl)}
                                                                alt="Sprite 3"
                                                                className="rounded bg-light border"
                                                                style={{ width: '38px', height: '38px', objectFit: 'contain' }}
                                                            />
                                                        )}
                                                        {t.spriteVictoryUrl && (
                                                            <img
                                                                src={resolveMediaUrl(t.spriteVictoryUrl)}
                                                                alt="Sprite 4"
                                                                className="rounded bg-light border"
                                                                style={{ width: '38px', height: '38px', objectFit: 'contain' }}
                                                            />
                                                        )}
                                                    </div>

                                                    <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                                                        <button
                                                            type="button"
                                                            className={`btn btn-sm ${isActive ? 'btn-outline-secondary' : 'btn-outline-success'} fw-bold py-1`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (onThemeSelected) onThemeSelected(t.id);
                                                            }}
                                                            disabled={isActive}
                                                        >
                                                            {isActive ? 'Tema Activo' : 'Activar Tema'}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger py-1"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteTheme(t.id, t.name);
                                                            }}
                                                            title="Eliminar tema"
                                                        >
                                                            <i className="bi bi-trash"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Columna Derecha: Formulario de Creación / Edición */}
                            <div className="col-12 col-lg-8">
                                {(isCreatingNew || editingThemeId) ? (
                                    <form onSubmit={handleSaveTheme} className="bg-white p-4 rounded-3 border shadow-sm">
                                        <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                                            <h6 className="fw-bold text-danger text-uppercase mb-0">
                                                {isCreatingNew ? '✨ Creando Nuevo Tema' : `✏️ Editando: ${themeForm.name}`}
                                            </h6>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => { setIsCreatingNew(false); setEditingThemeId(null); }}
                                            >
                                                Cancelar
                                            </button>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label fw-bold text-dark small">Nombre del Tema *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={themeForm.name}
                                                onChange={(e) => setThemeForm({ ...themeForm, name: e.target.value })}
                                                //placeholder="Ej. Tema Estadio Alfonso Lastras"
                                                required
                                            />
                                        </div>

                                        <div className="row g-3">
                                            {/* Fondo y Logo */}
                                            <div className="col-12 col-md-6">
                                                <AssetDropField
                                                    label="Fondo Móvil / Pantalla (1920x1080 WebP)"
                                                    assetType="background"
                                                    currentValue={themeForm.backgroundImageUrl}
                                                    onFileUploaded={handleUploadAsset}
                                                    isUploading={uploadingField === 'background'}
                                                    helpText="Optimizado automáticamente a WebP calidad 90."
                                                    onClear={() => setThemeForm({ ...themeForm, backgroundImageUrl: '' })}
                                                />
                                            </div>
                                            <div className="col-12 col-md-6">
                                                <AssetDropField
                                                    label="Logo del Juego (800x800 PNG Transparente)"
                                                    assetType="logo"
                                                    currentValue={themeForm.logoImageUrl}
                                                    onFileUploaded={handleUploadAsset}
                                                    isUploading={uploadingField === 'logo'}
                                                    helpText="PNG RGBA8888 con canal alfa."
                                                    onClear={() => setThemeForm({ ...themeForm, logoImageUrl: '' })}
                                                />
                                            </div>

                                            {/* Los 4 Sprites del Personaje */}
                                            <div className="col-12">
                                                <h6 className="fw-bold text-dark text-uppercase small mt-2 mb-2 border-bottom pb-1">
                                                    Sprites de Personaje (500x500 PNG RGBA8888)
                                                </h6>
                                            </div>

                                            <div className="col-6 col-md-3">
                                                <AssetDropField
                                                    label="1. Espalda (Espera)"
                                                    assetType="sprite_back"
                                                    currentValue={themeForm.spriteBackUrl}
                                                    onFileUploaded={handleUploadAsset}
                                                    isUploading={uploadingField === 'sprite_back'}
                                                    onClear={() => setThemeForm({ ...themeForm, spriteBackUrl: '' })}
                                                />
                                            </div>
                                            <div className="col-6 col-md-3">
                                                <AssetDropField
                                                    label="2. Frente (¡Frente!)"
                                                    assetType="sprite_front"
                                                    currentValue={themeForm.spriteFrontUrl}
                                                    onFileUploaded={handleUploadAsset}
                                                    isUploading={uploadingField === 'sprite_front'}
                                                    onClear={() => setThemeForm({ ...themeForm, spriteFrontUrl: '' })}
                                                />
                                            </div>
                                            <div className="col-6 col-md-3">
                                                <AssetDropField
                                                    label="3. Disparo (¡Fuego!)"
                                                    assetType="sprite_shoot"
                                                    currentValue={themeForm.spriteShootUrl}
                                                    onFileUploaded={handleUploadAsset}
                                                    isUploading={uploadingField === 'sprite_shoot'}
                                                    onClear={() => setThemeForm({ ...themeForm, spriteShootUrl: '' })}
                                                />
                                            </div>
                                            <div className="col-6 col-md-3">
                                                <AssetDropField
                                                    label="4. Victoria (Ganador)"
                                                    assetType="sprite_victory"
                                                    currentValue={themeForm.spriteVictoryUrl}
                                                    onFileUploaded={handleUploadAsset}
                                                    isUploading={uploadingField === 'sprite_victory'}
                                                    onClear={() => setThemeForm({ ...themeForm, spriteVictoryUrl: '' })}
                                                />
                                            </div>

                                            {/* Cintillos de Live Overlay */}
                                            <div className="col-12">
                                                <h6 className="fw-bold text-dark text-uppercase small mt-2 mb-2 border-bottom pb-1">
                                                    Cintillos para Live Overlay (OBS / vMix)
                                                </h6>
                                            </div>

                                            <div className="col-12 col-md-6">
                                                <AssetDropField
                                                    label="Cintillo 1536px (1536x180 WebP)"
                                                    assetType="banner_1536"
                                                    currentValue={themeForm.banner1536Url}
                                                    onFileUploaded={handleUploadAsset}
                                                    isUploading={uploadingField === 'banner_1536'}
                                                    helpText="Para transmisiones a 1536px de ancho."
                                                    onClear={() => setThemeForm({ ...themeForm, banner1536Url: '' })}
                                                />
                                            </div>
                                            <div className="col-12 col-md-6">
                                                <AssetDropField
                                                    label="Cintillo 1920px (1920x180 WebP)"
                                                    assetType="banner_1920"
                                                    currentValue={themeForm.banner1920Url}
                                                    onFileUploaded={handleUploadAsset}
                                                    isUploading={uploadingField === 'banner_1920'}
                                                    helpText="Para transmisiones Full HD a 1920px de ancho."
                                                    onClear={() => setThemeForm({ ...themeForm, banner1920Url: '' })}
                                                />
                                            </div>
                                        </div>

                                        <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary fw-bold"
                                                onClick={() => { setIsCreatingNew(false); setEditingThemeId(null); }}
                                            >
                                                Cancelar
                                            </button>
                                            <button
                                                type="submit"
                                                className="btn btn-danger fw-bold d-flex align-items-center gap-2"
                                                disabled={isSaving}
                                            >
                                                {isSaving ? (
                                                    <>
                                                        <div className="spinner-border spinner-border-sm" role="status"></div>
                                                        Guardando...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="bi bi-floppy-fill"></i> Guardar Tema
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                ) : (
                                    <div className="h-100 d-flex flex-column align-items-center justify-content-center bg-white p-5 rounded-3 border text-center text-muted">
                                        <i className="bi bi-images text-danger fs-1 mb-2"></i>
                                        <h6 className="fw-bold text-dark mb-1">Selecciona un tema o crea uno nuevo</h6>
                                        <p className="small mb-3">
                                            Puedes subir sprites de 4 estados para el personaje, fondos optimizados y cintillos para transmisión en vivo.
                                        </p>
                                        <button
                                            type="button"
                                            className="btn btn-danger fw-bold d-flex align-items-center gap-2"
                                            onClick={handleStartNew}
                                        >
                                            <i className="bi bi-plus-circle-fill"></i> Crear Nuevo Tema
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="modal-footer bg-light border-top">
                        <button type="button" className="btn btn-dark fw-bold px-4" onClick={onClose}>
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
